import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Trans, withTranslation } from 'react-i18next';
import {
    DaButton,
    DownloadIcon,
    DaLabel,
    CrossBoldIcon,
    InternalGrid,
    Message as UIMessage,
    Text,
    Link as PGLink,
} from '@paygreen/paygreen-ui';
import regexForm from '../../../utils/regexForm';
import { FileUploadContainer, FileUploadInput } from './uploadInputStyles';
import exampleFile from '../../../assets/files/export_example.csv';

const FormatedLink = ({ to, children }) => (
    <a href={to}>
        <PGLink>{children}</PGLink>
    </a>
);

const AddCsvProductForm = ({
    t,
    register,
    errors,
    onSubmit,
    children,
    tempFile,
    setTempFile,
    setErrorMessage,
    reset,
    errorMessage,
    setError,
}) => {
    const [isDragActive, setIsDragActive] = useState(false);
    const inputCsvRef = useRef(null);

    const removeFiles = () => {
        setErrorMessage();
        reset();
        setTempFile();
    };

    const convertBytesToKB = (bytes) => Math.round(bytes / 1000);

    const simulateInputClick = () => {
        inputCsvRef.current && inputCsvRef.current.click();
    };

    const addNewTempFile = (e) => {
        setErrorMessage();

        const { files: newFile } = e.target;
        newFile.length && setTempFile(newFile[0]);

        if (newFile[0].type !== 'text/csv') {
            setError('inputCsv', {
                type: 'string',
                message: t('form.error.inputCsv'),
            });
        } else if (newFile[0].size > 2000000)
            setError('inputCsv', {
                type: 'string',
                message: t(`form.error.inputCsvBig`),
            });
    };

    return (
        <>
            <form onSubmit={onSubmit} noValidate>
                <FileUploadContainer
                    isDragActive={isDragActive}
                    onDragOver={() => setIsDragActive(true)}
                    onDragLeave={() => setIsDragActive(false)}
                    onDrop={() => setIsDragActive(false)}
                >
                    <FileUploadInput
                        id="inputCsv"
                        name="inputCsv"
                        type="file"
                        accept=".csv"
                        ref={(e) => {
                            register(e, {
                                required: t('form.error.requiredFile'),
                                pattern: {
                                    value: regexForm.csvFile,
                                    message: t('form.error.inputCsv'),
                                },
                                validate: (value) =>
                                    value.length && value[0].size > 2000000
                                        ? t(`form.error.inputCsvBig`)
                                        : null,
                            });
                            inputCsvRef.current = e;
                        }}
                        onChange={addNewTempFile}
                    />

                    <DaLabel htmlFor="inputCsv">
                        <Text
                            textSize="base"
                            align="center"
                            marginBottom="xs"
                            colorWab="grey30"
                        >
                            {t('matching.addCsvForm.label')}
                        </Text>

                        <Text
                            textSize="base"
                            align="center"
                            marginBottom="xs"
                            colorWab="grey30"
                        >
                            {t('matching.addCsvForm.labelOr')}
                        </Text>
                    </DaLabel>

                    <button
                        type="button"
                        onClick={simulateInputClick}
                        style={{ pointerEvents: isDragActive ? 'none' : null }}
                    >
                        <DaButton
                            icon={<DownloadIcon />}
                            buttonSize="sm"
                            buttonStyle="line"
                        >
                            {t('matching.addCsvForm.inputButton')}
                        </DaButton>
                    </button>

                    <InternalGrid
                        childrenMarginTop={tempFile ? 'md' : 'sm'}
                        childrenMarginLateral="xs"
                        alignItems="center"
                        flexWrap="nowrap"
                    >
                        {tempFile ? (
                            <>
                                <Text colorWab="grey60" textSize="sm">
                                    {tempFile.name}
                                    &nbsp; ({convertBytesToKB(tempFile.size)}kB)
                                </Text>

                                <CrossBoldIcon
                                    htmlTag="button"
                                    colorPallet="status"
                                    colorStatus="danger"
                                    hasBackground={true}
                                    hasHover={true}
                                    iconSize="xs"
                                    onClick={() => removeFiles()}
                                    style={{ flexShrink: 0 }}
                                />
                            </>
                        ) : (
                            <Text colorWab="grey60" textSize="sm">
                                {t('matching.addCsvForm.noFile')}
                            </Text>
                        )}
                    </InternalGrid>
                </FileUploadContainer>

                <Text
                    marginBottom="xs"
                    textSize="xs"
                    colorPallet="wab"
                    colorWab="grey30"
                >
                    <Trans i18nKey="matching.addCsvForm.linkCsv">
                        ...
                        <FormatedLink to={exampleFile}>...</FormatedLink>
                        ...
                    </Trans>
                </Text>

                {errors.inputCsv && (
                    <UIMessage
                        arrowBlock="none"
                        colorStatus="warning"
                        hasStaticWidth={true}
                        marginTop="md"
                        marginBottom="sm"
                    >
                        <Text align="center" textSize="xs">
                            {errors.inputCsv.message}
                        </Text>
                    </UIMessage>
                )}

                {tempFile && !errors.inputCsv && !errorMessage && (
                    <Text
                        textSize="sm"
                        align="center"
                        marginBottom="xs"
                        marginTop="sm"
                        colorWab="grey60"
                    >
                        <Trans i18nKey="matching.addCsvForm.confirmSentence">
                            ...
                            <Text htmlTag="strong">
                                {{
                                    CsvName: tempFile.name,
                                }}
                            </Text>
                            ...
                        </Trans>
                    </Text>
                )}

                {children}
            </form>
        </>
    );
};

AddCsvProductForm.propTypes = {
    register: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
    setError: PropTypes.func.isRequired,
    /**
     * errors passed by react-hook-form for each field (necessary if you want errors management)
     */
    errors: PropTypes.object,
    tempFile: PropTypes.object,
    setTempFile: PropTypes.func,
    errorMessage: PropTypes.string,
    setErrorMessage: PropTypes.func,
};

AddCsvProductForm.defaultProps = {
    errors: {},
    tempFile: null,
    errorMessage: null,
};

export default withTranslation('common')(AddCsvProductForm);
